import { useState } from 'react';
import { observer } from 'mobx-react';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { useStores } from '../../hooks/useStores';
import theme from '../../utils/styles/theme';

const styles = () => ({
  root: {
    margin: 0,
    padding: theme.space[2],
  },
  title: {
    marginLeft: theme.space[2],
    textTransform: 'uppercase',
  },
  closeButton: {
    position: 'absolute',
    padding: theme.space[2],
    right: theme.space[1],
    top: theme.space[1],
    color: theme.colors.blacks[2],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography className={classes.title} variant="h6">
        {children}
      </Typography>

      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles({
  root: {
    padding: theme.space[3],
  },
  instructions: {
    color: theme.colors.blacks[4],
    lineHeight: 1.5,
    marginBottom: theme.space[2],
  },
  muted: {
    fontWeight: 'normal',
    color: theme.colors.blacks[4],
    marginBottom: theme.space[2],
  },
})((props) => {
  const { children, classes, ...other } = props;
  return (
    <MuiDialogContent className={classes.root}>
      {children({ classes, ...other })}
    </MuiDialogContent>
  );
});

const DialogActions = withStyles({
  root: {
    margin: 0,
    padding: theme.space[2],
  },
})(MuiDialogActions);

const ProductExportModal = () => {
  const { allProductsPage } = useStores();
  const [productExportJobCreated, setProductExportJobCreated] = useState(false);

  const handleStartExportClick = async () => {
    const toExport = allProductsPage.exportAllClicked
      ? []
      : allProductsPage.selectedProducts;
    const jobCreated = await allProductsPage.createProductExportJob(toExport);
    setProductExportJobCreated(jobCreated);
  };

  const closeDialog = () => {
    setProductExportJobCreated(false);
    allProductsPage.closeProductExportModal();
  };

  let productCountText = '1 product';
  if (
    allProductsPage.selectedProducts.length === 0 ||
    allProductsPage.exportAllClicked
  ) {
    productCountText = 'all products';
  } else if (allProductsPage.selectedProducts.length > 1) {
    productCountText = `${allProductsPage.selectedProducts.length} products`;
  }
  return (
    allProductsPage.isExportModalOpen && (
      <Dialog
        open={allProductsPage.isExportModalOpen}
        onExited={() => setProductExportJobCreated(false)}
      >
        <DialogTitle
          data-testid="product-export-dialog-title"
          onClose={closeDialog}
        >
          Export Products to CSV
        </DialogTitle>
        <DialogContent>
          {({ classes }) => {
            return !productExportJobCreated ? (
              <>
                <div
                  data-testid="product-export-dialog-instructions-1"
                  className={classes.instructions}
                >
                  You&apos;ve chosen to export {productCountText}.
                </div>
                <div
                  data-testid="product-export-dialog-instructions-2"
                  className={classes.instructions}
                >
                  Note that the format will be CSV with different headers than
                  your import files, so you can report on things like inventory
                  levels and access image links quickly.
                </div>
              </>
            ) : (
              <div className={classes.muted}>
                You will receive an email with a link to download the CSV with{' '}
                {productCountText} when it&apos;s ready. At peak times this
                could take several minutes.
              </div>
            );
          }}
        </DialogContent>
        {!productExportJobCreated && (
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button color="primary" onClick={handleStartExportClick}>
              Start Export
            </Button>
          </DialogActions>
        )}
      </Dialog>
    )
  );
};

export default observer(ProductExportModal);
